import styled from 'styled-components';
import type { SurfaceRole } from '@/theme/tokens';
import { surfaceGradient } from '@/theme/surfaceGradient';
import { softCardInteraction } from '@/theme/interactions';

/**
 * ConsolePanel — base card surface for the shell (formerly GlassPanel).
 * Opaque `surface` by default, which occludes the animated background so the reading
 * column stays legible. When the user turns on glass cards (Appearance → "Card
 * Appearance" → Glass cards), it switches to a translucent `glassBg` + blur so the
 * background shows through.
 *
 * The card's colour and material both come from Appearance → Card Appearance:
 * `theme.color.surface` (the contrast-safe resolved card colour) plus
 * `theme.material.*` (sheen / grain layers and the elevation shadow).
 */
const glassFilter = (blur: number, sat: number): string => `blur(${blur}px) saturate(${sat})`;

export const ConsolePanel = styled.div<{ $elevated?: boolean; $surfaceRole?: SurfaceRole }>`
  /* LONGHAND, NOT the \`background\` shorthand. The material layers composite on top of
     this opaque fill; the shorthand would silently wipe background-image, deleting grain
     and sheen with no error. Any card variant that reaches for \`background:\` loses them. */
  background-color: ${({ theme, $surfaceRole }) =>
    theme.cardsGlass
      ? surfaceGradient(theme, $surfaceRole) === 'none'
        ? theme.color.glassBg
        : 'transparent'
      : theme.color.surface};
  background-image: ${({ theme, $surfaceRole }) =>
    `${theme.material.backgroundImage}, ${surfaceGradient(theme, $surfaceRole)}`};
  background-size: ${({ theme }) => `${theme.material.backgroundSize}, auto`};
  background-repeat: ${({ theme }) => `${theme.material.backgroundRepeat}, no-repeat`};
  /* Glass blur is user-capped at 24px: backdrop-filter re-blurs every frame the
     content behind it changes, so bounding it keeps scrolling a feed over the
     animated backdrop cheap (and avoids the iOS Safari position:fixed jank bug). */
  backdrop-filter: ${({ theme }) =>
    theme.cardsGlass ? glassFilter(theme.glassBlur, theme.glassSaturation) : 'none'};
  -webkit-backdrop-filter: ${({ theme }) =>
    theme.cardsGlass ? glassFilter(theme.glassBlur, theme.glassSaturation) : 'none'};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  box-shadow: ${({ theme, $elevated }) =>
    $elevated ? theme.material.shadowElevated : theme.material.shadow};
  padding: 16px;
  ${softCardInteraction}
`;
